import { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { notificationsAPI } from '@/api/notifications';

/**
 * Notification Context
 * Manages user notifications and unread count
 */
const NotificationContext = createContext(null);

/**
 * NotificationProvider component
 * Wraps the application to provide notification state and functions
 */
export function NotificationProvider({ children }) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  
  /**
   * Fetch all notifications for a user
   */
  const fetchNotifications = useCallback(async (userId) => {
    setLoading(true);
    try {
      const data = await notificationsAPI.getNotifications(userId);
      setNotifications(data);
      
      const unread = data.filter(n => !n.read).length;
      setUnreadCount(unread);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, []);
  
  /**
   * Mark notification as read
   */
  const markAsRead = useCallback(async (notificationId) => {
    try {
      await notificationsAPI.markAsRead(notificationId);
      
      setNotifications(prev => prev.map(n =>
        n.id === notificationId ? { ...n, read: true } : n
      ));
      
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  }, []);
  
  /**
   * Mark all notifications as read
   */
  const markAllAsRead = useCallback(async (userId) => {
    try {
      await notificationsAPI.markAllAsRead(userId);
      
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
      throw error;
    }
  }, []);
  
  // Memoize context value
  const value = useMemo(() => ({
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    markAsRead,
    markAllAsRead
  }), [notifications, unreadCount, loading, fetchNotifications, markAsRead, markAllAsRead]);
  
  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

/**
 * useNotification hook
 * Access notification context in components
 * @throws {Error} If used outside NotificationProvider
 */
export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within NotificationProvider');
  }
  return context;
};
